/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';

    export interface IEntity {
        href: string;
    }

    export interface INamedEntity extends IEntity {
        name?: string;
    }
          
    export interface ITopologyElement extends INamedEntity {
        x?: number;
        y?: number;
        class?: () => TopologyElementType;
        getDisplayImage?: () => string;
        health?: IDataNodeHealth;
        canWrite?: boolean;
    }

    export interface IAllTopologyElements {
        networkNodes?: INetworkNode[];
        networks?: INetwork[];
        gateways?: IGateway[];
        links?: ILink[];
        probes?: IProbe[];
        hotspots?: IHotspot[];
        sonNodes?: ISonNode[];
        sonLinks?: ISonLink[];
        multiblocks?: MultiBlock[];
    }
    
    export enum TopologyElementType {
        NetworkNode = 0,
        Network = 1,
        Gateway = 2,
        Link = 3,
        Probe = 4,
        Hotspot = 5,
        Switch = 6,
        SonLink = 7,
        SonNetwork = 8,
        MultiBlock = 9,
    }

    export const topologyElementTypeToHref: string[]  = [
        "networkNodes",
        "networks",
        "gateways",
        "links",
        "probes",
        "hotspots",
        "switches",
        "sonLinks",
        "networks",
        "multiblocks",
    ];
    
    export class TopologyElement implements ITopologyElement {
        public href: string;
        public name: string;
        public x: number;
        public y: number;
        public health: IDataNodeHealth;

        /**
         * Gets an backend url and returns its topology element type
         * @param {string} href - Element backend URL
         */
        public static getElementTypeByHref(href: string): TopologyElementType {
            if (href.indexOf('/networkNodes') > -1)
                return TopologyElementType.NetworkNode;
            else if (href.indexOf('/networks') > -1)
                return TopologyElementType.Network;
            else if (href.indexOf('/gateways') > -1)
                return TopologyElementType.Gateway;
            else if (href.indexOf('/links') > -1)
                return TopologyElementType.Link;
            else if (href.indexOf('/probes') > -1)
                return TopologyElementType.Probe;
            else if (href.indexOf('/hotspots') > -1)
                return TopologyElementType.Hotspot;
            else if (href.indexOf(SonNode.switchesPreHref) > -1)
                return TopologyElementType.Switch;
            else if (href.indexOf(SonLink.sonLinkPreHref) > -1)
                return TopologyElementType.SonLink;
            else if (href.indexOf('/multiBlocks') > -1)
                return TopologyElementType.MultiBlock;
            else
                return undefined;
        }

        /**
         * Gets an backend url and returns its topology element type as a string
         * @param {string} href - Element backend URL
         */
        public static getMapElementTypeAsStringByHref(href: string): string {
            if (!href)
                return undefined;
            else if (href.indexOf('/networkNodes') > -1)
                return 'network node';
            else if (href.indexOf('/networks') > -1)
                return 'network';
            else if (href.indexOf('/gateways') > -1)
                return 'gateway';
            else if (href.indexOf('/links') > -1)
                return 'link';
            else if (href.indexOf('/probes') > -1)
                return 'probe';
            else if (href.indexOf('/hotspots') > -1)
                return 'hotspot';
            else if (href.indexOf(SonNode.switchesPreHref) > -1)
                return 'SON switch';
            else if (href.indexOf(SonLink.sonLinkPreHref) > -1)
                return 'SON link';
            else if (href.indexOf('/multiBlocks') > -1)
                return 'multiblocks';
            else
                return undefined;
        }

        /**
         * Gets a TopologyElementType and returns it as a string
         * @param {TopologyElementType} type - Element type
         */
        public static getMapElementTypeAsStringByElementType(type: TopologyElementType): string {
            if (type === TopologyElementType.NetworkNode)
                return 'network node';
            else if (type === TopologyElementType.Network)
                return 'network';
            else if (type === TopologyElementType.Gateway)
                return 'gateway';
            else if (type === TopologyElementType.Link)
                return 'link';
            else if (type === TopologyElementType.Probe)
                return 'probe';
            else if (type === TopologyElementType.Hotspot)
                return 'hotspot';
            else if (type === TopologyElementType.Switch)
                return 'SON switch';
            else if (type === TopologyElementType.SonLink)
                return 'SON link';
            else if (type === TopologyElementType.MultiBlock)
                return 'multi block';
            else
                return undefined;
        }

        /**
         * Gets a topology element and returns its type
         * @param {element} ITopologyElement - Element backend URL
         */
        public static getElementType(element: ITopologyElement): TopologyElementType {
            return this.getElementTypeByHref(element.href);
        }

        /**
         * Gets a topology element and returns its type
         * @param {element} ITopologyElement - Element backend URL
         */
        public static getElementTypeAsString(element: ITopologyElement): string {
            return this.getMapElementTypeAsStringByHref(element.href);
        }

        /**
         * 
         */
        public static areLinkableElements(href1: string, href2: string): boolean {
            let type1: TopologyElementType = TopologyElement.getElementTypeByHref(href1);
            let type2: TopologyElementType = TopologyElement.getElementTypeByHref(href2);
            // network nodes can only be linked to networks
            if (type1 === TopologyElementType.NetworkNode || type2 === TopologyElementType.NetworkNode) {
                return type2 === TopologyElementType.Network || type1 === TopologyElementType.Network;
            // gateways can only be linked to networks
            } else if (type1 === TopologyElementType.Gateway || type2 === TopologyElementType.Gateway) {
                return type2 === TopologyElementType.Network || type1 === TopologyElementType.Network;
            // probes can only be linked to networks
            } else if (type1 === TopologyElementType.Probe || type2 === TopologyElementType.Probe) {
                return type2 === TopologyElementType.Network || type1 === TopologyElementType.Network;
            // hotspots can only be linked to networks
            } else if (type1 === TopologyElementType.Hotspot || type2 === TopologyElementType.Hotspot) {
                return type2 === TopologyElementType.Network || type1 === TopologyElementType.Network;
            // otherwise the link is not possible
            } else {
                //
            }
        }

        public static find(array: TopologyElement[], href: string): {index: number, element: TopologyElement} {
            let id: number = Common.Utils.idFromHref(href);
            if (array !== undefined && array.length > 0)
                for (let i: number = 0; i < array.length; i++)
                    if (Common.Utils.idFromHref(array[i].href) === id)
                        return {index: i, element: array[i]};
            return undefined;
        }

        public static findById(array: TopologyElement[], id: number): {index: number, element: TopologyElement} {
            if (array !== undefined && array.length > 0)
                for (let i: number = 0; i < array.length; i++)
                    if (Common.Utils.idFromHref(array[i].href) === id)
                        return {index: i, element: array[i]};
            return undefined;
        }

        public static findIndex(array: TopologyElement[], href: string): number {
            let res: {index: number, element: TopologyElement} = TopologyElement.find(array, href);
            return (res !== undefined && res.index !== undefined && res.index > -1) ? res.index : undefined;
        }

        public static findIndexById(array: TopologyElement[], id: number): number {
            let res: {index: number, element: TopologyElement} = TopologyElement.findById(array, id);
            return (res !== undefined && res.index !== undefined && res.index > -1) ? res.index : undefined;
        }

        public static findElement(array: TopologyElement[], href: string): TopologyElement {
            let res: {index: number, element: TopologyElement} = TopologyElement.find(array, href);
            return (res !== undefined && res.element !== undefined) ? res.element : undefined;
        }

        public static findElementInAll(all: Models.IAllTopologyElements, href: string): TopologyElement {
            let res: TopologyElement = TopologyElement.findElement(all.networkNodes as TopologyElement[], href);
            if (res)
                return res;
            res = TopologyElement.findElement(all.networks as TopologyElement[], href);
            if (res)
                return res;
            res = TopologyElement.findElement(all.gateways as TopologyElement[], href);
            if (res)
                return res;
            res = TopologyElement.findElement(all.hotspots as TopologyElement[], href);
            if (res)
                return res;
            res = TopologyElement.findElement(all.probes as TopologyElement[], href);
            if (res)
                return res;
            res = TopologyElement.findElement(all.sonLinks as TopologyElement[], href);
            if (res)
                return res;
            res = TopologyElement.findElement(all.sonNodes as TopologyElement[], href);
            return res;
        }

        public static findLinksByFromToId(array: ILink[], fromTo: string): ILink[] {
            if (array !== undefined && array.length > 0)
                return array.filter((l: ILink) => {
                    return l.fromTo === fromTo;
                });
            return undefined;
        }

        public static findMultiBlocksByFromToId(array: MultiBlock[], fromTo: string): MultiBlock[] {
            if (array !== undefined && array.length > 0)
                return array.filter((mb: MultiBlock) => {
                    return mb.leftBlock.networkNodeHref + '-MULTIBLOCK-' + mb.rightBlock.networkNodeHref === fromTo;
                });
        }

        /**
         * TopologyElement's empty constructor for automatic conversion from REST data.
         */
        constructor() {
            this.href = '';
            this.name = '';
            this.x = undefined;
            this.y = undefined;
            this.health = {
                data: {
                    healthStatus: 'PENDING',
                    checks: [],
                    elementId: '',
                },
            };
        }

        public getDisplayImage(): string {
            return 'images/unknown.png';
        }

        public class(): TopologyElementType {
            // to be overrided
            return undefined;
        }
    }

    export interface ITopologySearchResult {
        topologyElement: ITopologyElement;
        simplifiedHref: string;
        topologyElementType: string;
        fullText: string;
        icon: string;
    }
}
